import { z } from 'zod';
import { positiveDecimal, rate } from './primitives.js';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { error: 'Date attendue (AAAA-MM-JJ)' });
const note = z.string().trim().max(2000);
/** Précision des colonnes : un taux ou un avancement plus fin serait arrondi en base, donc recalculé autrement. */
const billingRate = rate.refine((v) => !/\.\d{7,}$/.test(v), { error: 'Six décimales au plus pour un taux' });
const percent = positiveDecimal.refine((v) => !/\.\d{5,}$/.test(v), { error: 'Quatre décimales au plus pour un avancement' });

/** Natures de document créées directement ; l'avoir part d'une facture validée. */
export const CREATABLE_INVOICE_KINDS = ['STANDARD', 'DEPOSIT', 'PROGRESS', 'INTERMEDIATE', 'FINAL', 'STATEMENT_PROVISIONAL', 'STATEMENT_FINAL', 'RETENTION_RELEASE'] as const;

export const INVOICE_KIND_LABELS: Record<(typeof CREATABLE_INVOICE_KINDS)[number] | 'CREDIT', string> = {
  STANDARD: 'Facture',
  DEPOSIT: "Facture d'acompte",
  PROGRESS: 'Facture de situation',
  INTERMEDIATE: 'Facture intermédiaire',
  FINAL: 'Facture définitive',
  STATEMENT_PROVISIONAL: 'Décompte provisoire',
  STATEMENT_FINAL: 'Décompte définitif',
  RETENTION_RELEASE: 'Libération de retenue de garantie',
  CREDIT: 'Avoir',
};

/** Termes modifiables d'un brouillon. */
const draftTerms = {
  issueDate: isoDate.optional(),
  dueDate: isoDate.nullable().optional(),
  notes: note.nullable().optional(),
  discountRate: billingRate.optional(),
  retentionRate: billingRate.optional(),
  retentionBase: z.enum(['HT', 'TTC']).optional(),
  /** Déduction d'acompte choisie ; absente : proposition automatique. */
  depositDeductionHt: positiveDecimal.nullable().optional(),
  /** Avancement cumulé (en %) par ligne du devis : situations, intermédiaires, décomptes provisoires. */
  progress: z.record(z.string(), percent).optional(),
  /** Acompte : montant HT, ou pourcentage du marché. */
  depositAmountHt: positiveDecimal.optional(),
  depositPercent: positiveDecimal.optional(),
};

export const invoiceCreateSchema = z.object({ quotationId: z.uuid(), kind: z.enum(CREATABLE_INVOICE_KINDS), ...draftTerms });
export const invoiceUpdateSchema = z.object({ version: z.number().int().positive(), ...draftTerms });

export const creditCreateSchema = z.object({
  reason: z.string().trim().min(3, { error: 'Indiquez le motif de cet avoir' }).max(500),
  /** Absent : avoir total. Sinon, montant HT à annuler par ligne de la facture d'origine. */
  lines: z.array(z.object({ invoiceLineId: z.uuid(), amount: positiveDecimal })).min(1).optional(),
});

export const paymentCreateSchema = z.object({
  direction: z.enum(['IN', 'REFUND']).default('IN'),
  amount: positiveDecimal.refine((v) => Number(v) > 0, { error: 'Le montant doit être supérieur à zéro' }),
  paidOn: isoDate,
  methodCode: z.string().trim().min(1).max(40),
  reference: z.string().trim().max(120).nullable().optional(),
  note: z.string().trim().max(500).nullable().optional(),
});
export const paymentCancelSchema = z.object({ reason: z.string().trim().min(3, { error: "Indiquez le motif de l'annulation" }).max(500) });

export const paymentMethodCreateSchema = z.object({ label: z.string().trim().min(2).max(60) });
export const paymentMethodUpdateSchema = z.object({ label: z.string().trim().min(2).max(60).optional(), active: z.boolean().optional() });

/** Changement manuel de statut d'un devis. « Finalisé » et « Facturé » sont posés par l'application. */
export const quotationStatusSchema = z.object({
  status: z.enum(['SENT', 'ACCEPTED', 'REJECTED']).optional(),
  validUntil: isoDate.nullable().optional(),
});
